import { bridge } from "./bridge";
import { bridge as vestBridge } from "./bridge-vest";
import { getAccessToken as getAccessTokenFromApp } from './adapt-index';
import type { GuestLoginResult } from './adapt-index';
import { FreshToken, AccessToken } from "./const";
import { Env } from '@/libs/env';

// 如果存在 comeAppOpJSB，优先走马甲包桥接；否则走默认 bridge（React Native WebView）
export const isVestEnv = !!(typeof window !== 'undefined' && (window as any).comeAppOpJSB);
export const comBridge = isVestEnv ? vestBridge : bridge;

export interface DeviceInfo {
  appId: string;
  appName: string;
  appVersion: string;
  model: string;
  deviceBrand: string;
  systemName: string;
  systemVersion: string;
}
export const getDeviceInfo = () => comBridge.invoke<DeviceInfo>('getDeviceInfo');

export const getDeviceId = async () => {
  const deviceId = await comBridge.invoke<string>('getDeviceId');
  if (deviceId && !deviceId.startsWith('COME_DEVICE_')) {
    return `COME_DEVICE_${deviceId}`;
  }
  return deviceId;
};

export const getAppInfo = () => comBridge.invoke('getAppInfo');

export const getUserInfo = () => comBridge.invoke('getUserInfo');

export const getAFID = () => comBridge.invoke<string>('getAFId');
export const getChannelKey = () => comBridge.invoke<string>('getChannelKey'); // 渠道标识

export const closeWebView = () => comBridge.invoke('closeWebView');

export const setAccessToken = (token: string) => isVestEnv ? vestBridge.setStorage(AccessToken, token) : comBridge.invoke('setAccessToken', token);

export const getAccessToken = () => isVestEnv ? vestBridge.getStorage(AccessToken) : comBridge.invoke<string>('getAccessToken');

const getLocalServerInfo = () => comBridge.invoke<{ host: string; port: number; }>('getServerInfo');

const getImage = async (image: Blob, fileName: string) => {
  const { port } = await getLocalServerInfo();
  const formData = new FormData();
  formData.append('uploadedFile', image);
  formData.append('fileName', fileName);
  return fetch(`http://localhost:${port}/upload`, { method: 'POST', body: formData }).then(res => res.json());
};

export enum UserType {
  Unknown = 0,
  Guest = 1,
  BlackUser = 2,
  NonGuest = 3,
}

export const getUserType = () => comBridge.invoke<UserType>('getUserType');

export const getFreshToken = () => isVestEnv ? comBridge.invoke<string>('getStorage', { key: FreshToken }) : comBridge.invoke<string>('getFreshToken');

export const setFreshToken = (token: string) => isVestEnv ? comBridge.invoke<string>('setStorage', { key: FreshToken, value: token }) : comBridge.invoke('setFreshToken', token);

export const guestLogin = async (): Promise<GuestLoginResult> => {
  if (isVestEnv) {
    return getAccessTokenFromApp(true);
  }
  const raw = await comBridge.invoke<string | GuestLoginResult>('guestLogin');
  // 旧版 APK 返回 token 字符串，新版返回 GuestLoginResult 对象
  if (typeof raw === 'string') {
    return { token: raw || '' };
  }
  return { token: raw?.token || '', loginStatus: raw?.loginStatus };
};

export const saveImage = async (image: Blob, fileName: string) => {
  return getImage(image, fileName).then(res =>
    comBridge.invoke<boolean>('saveImage', { fileName, path: res.data.path })
  ).catch(e => {
    console.error('save image failed', e);
    return false;
  });
};

export const saveImageVest = async (image: string) => {
  try {
    return comBridge.invoke<boolean>('saveImage', image);
  } catch (e) {
    console.error('save image failed (vest env)', e);
    return false;
  }
};

export const share = async (data: Partial<{ title: string, link: string; description: string; image: Blob; type: 'whatsapp' | 'telegram' | 'facebook'; }>) => {
  return comBridge.invoke<boolean>('share', data);
};
export const getInsets = () => comBridge.invoke<{ topSafe: number; bottomSafe: number; }>('getInsets');

export const openUrlInBrowser = (url: string) => comBridge.invoke<boolean>('openUrlInBrowser', url);
export const openUrlInWebView = (url: string) => comBridge.invoke('openUrlInWebView', url);
// 主apk
export const isInAppOwn = () => typeof comBridge.getJSI() === 'function';
// 马甲包或主apk
export const isInApp = () => typeof comBridge.getJSI() === 'function' || isVestEnv;
// 马甲包
export const isInVest = () => typeof window !== 'undefined' && (window as any).comeAppOpJSB;

export const onLoginSuccess = (userId: string) => comBridge.invoke('loginSuccess', userId);

export const getFcmToken = () => vestBridge.invoke('getFcmToken');

export const updateApk = (url: string, onProgress: (progress: number) => void) => {
  return new Promise((resolve, reject) => {

    bridge.invoke('updateApk', { url, fileName: `${Env.NEXT_PUBLIC_APP_NAME}.apk` });

    bridge.register('apkUpdateProgress', (payload) => {
      console.log('appUpdate', payload.status, payload.progress);
      if (payload.progress === 100) {
        bridge.unregister('apkUpdateProgress', onProgress);
        resolve(true);
      } else if (['failed', 'error'].includes(payload.status)) {
        bridge.unregister('apkUpdateProgress', onProgress);
        reject(payload.message);
      }
      onProgress(payload.progress);
    });
  });
};

export type ResBizType = 'rummy';
export const startUpdateResource = async (bizType: ResBizType) => {
  const updateType = await bridge.invoke<number>('startUpdateResource', { bizType });
  const promise = new Promise((resolve, reject) => {
    bridge.once('resourceUpdated', (err) => {
      if (err) {
        reject(err);
      } else {
        resolve(true);
      }
    });
  });
  return {
    updateType, // 1-强更 2-可选
    waitForUpated: () => promise
  };
};

export const history = {
  push: (url: string) => comBridge.invoke('pushHistory', url),
  pop: () => comBridge.invoke('popHistory'),
};

export interface AssetJsonChannel {
  channel: string;
}

// 读取 App 内置 asset JSON 文件，返回解析后的对象或 null
// 业务用法：readAssetJson('channel11') 获取渠道信息
export const readAssetJson = <T = AssetJsonChannel>(key: string) =>
  comBridge.invoke<T | null>('readAssetJson', key);

export const emit = comBridge.emit.bind(comBridge);
export const addEventListener = comBridge.register.bind(comBridge);
export const removeEventListener = comBridge.unregister.bind(comBridge);  